/**
 * Context Sidebar Component
 *
 * Shows discovery progress with:
 * - Phase progress carousel
 * - Question overview with status
 * - Answer summaries
 * - Documents/Sources panel (unified facet-first)
 */

import { useState, useCallback } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Circle,
  FileText,
  Link2,
  Plus,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Upload,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  useDiscoveryAssistantStore,
  useCurrentQuestion,
  useProgress,
} from '@/stores/discoveryAssistantStore'
import { DISCOVERY_QUESTIONS, PHASE_CONFIG } from '@/lib/ai/conversationAI'
import { QualityBadge } from './QualityIndicator'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { sourcesApi } from '@/lib/api/client'
import { UnifiedDocumentUploader } from '@/components/document-processing'
import type { AnalysisResult } from '@/hooks/useDocumentAnalyzer'

interface ContextSidebarProps {
  accountId: string
  projectId: string
  className?: string
}

type Phase = keyof typeof PHASE_CONFIG

// Map facet types to display styles
const FACET_STYLE_MAP: Record<string, { label: string; color: string }> = {
  customer_segment: { label: 'Segment', color: 'bg-business/20 text-business' },
  customer_pain: { label: 'Pain', color: 'bg-solution/20 text-solution' },
  problem: { label: 'Problem', color: 'bg-solution/20 text-solution' },
  job_to_be_done: { label: 'Job', color: 'bg-market/20 text-market' },
  competitor: { label: 'Competitor', color: 'bg-warm-100 text-warm-700' },
  feature: { label: 'Feature', color: 'bg-business/20 text-business' },
  value_proposition: { label: 'Value', color: 'bg-market/20 text-market' },
  opportunity: { label: 'Opportunity', color: 'bg-green-100 text-green-700' },
  metric: { label: 'Metric', color: 'bg-blue-100 text-blue-700' },
}

// Get facet display style helper
function getFacetStyle(facetTypeId: string) {
  const key = Object.keys(FACET_STYLE_MAP).find((k) =>
    facetTypeId.toLowerCase().includes(k.toLowerCase())
  )
  return key ? FACET_STYLE_MAP[key] : { label: facetTypeId, color: 'bg-muted text-muted-foreground' }
}

// Component to fetch and display facets for a source when expanded
function SourceFacetsList({
  accountId,
  projectId,
  sourceId
}: {
  accountId: string
  projectId: string
  sourceId: string
}) {
  const { data: sourceWithFacets, isLoading } = useQuery({
    queryKey: ['sources', 'detail', accountId, projectId, sourceId],
    queryFn: () => sourcesApi.get(accountId, projectId, sourceId),
    enabled: !!accountId && !!projectId && !!sourceId,
    staleTime: 60 * 1000,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-warm-400" />
      </div>
    )
  }

  const facets = sourceWithFacets?.facets ?? []

  if (facets.length === 0) {
    return (
      <div className="text-center text-xs text-warm-500 py-2">
        No insights extracted yet
      </div>
    )
  }

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {facets.slice(0, 5).map((facet) => {
        const style = getFacetStyle(facet.facetTypeId)
        return (
          <div
            key={facet.id}
            className="flex items-start gap-2 text-xs p-2 bg-white rounded"
          >
            <Badge className={`text-[9px] flex-shrink-0 ${style.color}`}>
              {style.label}
            </Badge>
            <span className="text-warm-700 line-clamp-2">{facet.content}</span>
          </div>
        )
      })}
      {facets.length > 5 && (
        <div className="text-center text-xs text-warm-500 py-2">
          +{facets.length - 5} more insights
        </div>
      )}
    </div>
  )
}

export function ContextSidebar({ accountId, projectId, className }: ContextSidebarProps) {
  const { session, answers, sources, advanceToNext, addSource } = useDiscoveryAssistantStore()
  const currentQuestion = useCurrentQuestion()
  const progress = useProgress()
  const queryClient = useQueryClient()

  const [activeCarouselPhase, setActiveCarouselPhase] = useState<Phase>('market_reality')
  const [showAddSource, setShowAddSource] = useState(false)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [sourceType, setSourceType] = useState<'url' | 'text'>('url')
  const [sourceTitle, setSourceTitle] = useState('')
  const [sourceContent, setSourceContent] = useState('')
  const [isAddingSource, setIsAddingSource] = useState(false)
  const [expandedSourceIds, setExpandedSourceIds] = useState<Set<string>>(new Set())

  // Handle document analysis completion (from UnifiedDocumentUploader)
  const handleDocumentAnalyzed = useCallback((result: AnalysisResult) => {
    console.log('[ContextSidebar] Document analyzed:', result.source.title)
    console.log(`Found ${result.facets.length} facets in ${result.analysisTime}ms`)
    // Invalidate sources query to refresh the list
    queryClient.invalidateQueries({ queryKey: ['sources', 'list', accountId, projectId] })
    setShowUploadDialog(false)
  }, [queryClient, accountId, projectId])

  // Fetch sources for this project
  const { data: sourcesData, isLoading: sourcesLoading } = useQuery({
    queryKey: ['sources', 'list', accountId, projectId],
    queryFn: () => sourcesApi.list(accountId, projectId),
    enabled: !!accountId && !!projectId,
  })

  // Get sources tagged for discovery assistant
  const discoveryAssistantSources = sourcesData?.filter(
    (s) => s.tags?.includes('discovery-assistant') || s.tags?.includes('discovery-intake')
  ) ?? []

  const phases: Phase[] = ['market_reality', 'competitive_landscape', 'value_design', 'business_viability']
  const currentPhaseIndex = phases.indexOf(activeCarouselPhase)

  const goToPrevPhase = () => {
    if (currentPhaseIndex > 0) {
      setActiveCarouselPhase(phases[currentPhaseIndex - 1])
    }
  }

  const goToNextPhase = () => {
    if (currentPhaseIndex < phases.length - 1) {
      setActiveCarouselPhase(phases[currentPhaseIndex + 1])
    }
  }

  // Get questions for current carousel phase
  const phaseConfig = PHASE_CONFIG[activeCarouselPhase]
  const phaseQuestions = DISCOVERY_QUESTIONS.filter((q) => q.phase === activeCarouselPhase)

  // Handle advancing to next question
  const handleAdvance = async () => {
    if (!session) return
    await advanceToNext(accountId, projectId)
  }

  // Handle adding a manual source (URL/text)
  const handleAddSource = async () => {
    if (!sourceTitle.trim() || !sourceContent.trim()) return
    setIsAddingSource(true)
    try {
      await addSource(accountId, projectId, {
        sourceType,
        title: sourceTitle.trim(),
        content: sourceContent.trim(),
      })
      setSourceTitle('')
      setSourceContent('')
      setShowAddSource(false)
    } catch (error) {
      console.error('Failed to add source:', error)
    } finally {
      setIsAddingSource(false)
    }
  }

  // Toggle source expansion
  const toggleSourceExpansion = (sourceId: string) => {
    setExpandedSourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(sourceId)) {
        next.delete(sourceId)
      } else {
        next.add(sourceId)
      }
      return next
    })
  }

  return (
    <div
      className={cn(
        'flex flex-col h-full min-h-0 min-w-0 bg-white rounded-lg shadow-sm border border-warm-200',
        className
      )}
    >
      {/* Progress header */}
      <div className="p-4 border-b border-warm-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-warm-900">Discovery Progress</h3>
          <span className="text-sm text-warm-600">{progress.completed}/12</span>
        </div>

        {/* Overall progress bar */}
        <div className="h-2 bg-warm-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>

        {/* Phase indicators */}
        <div className="flex gap-1 mt-3">
          {phases.map((phase) => {
            const config = PHASE_CONFIG[phase]
            const isActive = session?.currentPhase === phase
            const completedInPhase =
              session?.completedQuestions.filter((q) => config.questions.includes(parseInt(q)))
                .length || 0
            const isComplete = completedInPhase === 3

            return (
              <button
                key={phase}
                onClick={() => setActiveCarouselPhase(phase)}
                className={cn(
                  'flex-1 h-1.5 rounded-full transition-all',
                  activeCarouselPhase === phase && 'ring-2 ring-offset-1',
                  isComplete ? 'bg-green-500' : isActive ? 'bg-primary' : 'bg-warm-300'
                )}
                style={{
                  ['--tw-ring-color' as string]: config.color,
                }}
                title={config.label}
              />
            )
          })}
        </div>
      </div>

      {/* Phase carousel */}
      <div className="p-4 border-b border-warm-200">
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevPhase}
            disabled={currentPhaseIndex === 0}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="text-center">
            <h4 className="font-semibold" style={{ color: phaseConfig.color }}>
              {phaseConfig.label}
            </h4>
            <p className="text-xs text-warm-500">Phase {currentPhaseIndex + 1} of 4</p>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextPhase}
            disabled={currentPhaseIndex === phases.length - 1}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Questions in this phase */}
        <div className="space-y-2">
          {phaseQuestions.map((question) => {
            const answer = answers.get(String(question.number))
            const isCurrentQuestion = session?.currentQuestion === String(question.number)
            const isCompleted = session?.completedQuestions.includes(String(question.number))

            return (
              <div
                key={question.number}
                className={cn(
                  'p-3 rounded-lg border transition-all',
                  isCurrentQuestion && 'border-primary bg-primary/5 ring-1 ring-primary/20',
                  isCompleted && !isCurrentQuestion && 'border-green-200 bg-green-50',
                  !isCurrentQuestion && !isCompleted && 'border-warm-200 bg-warm-50'
                )}
              >
                <div className="flex items-start gap-2">
                  {/* Status icon */}
                  <div className="mt-0.5">
                    {isCompleted ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : isCurrentQuestion ? (
                      <Circle className="h-4 w-4 text-primary fill-primary" />
                    ) : (
                      <Circle className="h-4 w-4 text-warm-400" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-warm-500">Q{question.number}</span>
                      {answer?.qualityScore && <QualityBadge score={answer.qualityScore} />}
                    </div>
                    <p className="text-sm text-warm-700 line-clamp-2">{question.question}</p>

                    {/* Answer preview */}
                    {answer && (
                      <p className="text-xs text-warm-500 mt-1 line-clamp-1">{answer.answer}</p>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Action buttons */}
      {session && currentQuestion && (
        <div className="p-4 border-b border-warm-200">
          <Button
            onClick={handleAdvance}
            disabled={!answers.has(session.currentQuestion)}
            className="w-full"
          >
            Continue to Next Question
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
          {!answers.has(session.currentQuestion) && (
            <p className="text-xs text-warm-500 mt-2 text-center">
              Answer the current question to proceed
            </p>
          )}
        </div>
      )}

      {/* Documents section - using facet-first sources */}
      <div className="p-4 border-b border-warm-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-warm-800">Documents</h4>
          <div className="flex items-center gap-1">
            <span className="text-xs text-warm-500">{discoveryAssistantSources.length}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="h-3 w-3 mr-1" />
              Upload
            </Button>
          </div>
        </div>

        {sourcesLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-warm-400" />
          </div>
        ) : discoveryAssistantSources.length === 0 ? (
          <div className="text-center py-4 text-warm-500">
            <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">Upload documents to extract insights</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {discoveryAssistantSources.slice(0, 10).map((source) => {
              const isExpanded = expandedSourceIds.has(source.id)
              const facetCount = source.facetStats?.total || 0

              return (
                <div key={source.id} className="border border-warm-200 rounded-lg overflow-hidden">
                  {/* Source header */}
                  <button
                    onClick={() => toggleSourceExpansion(source.id)}
                    className="w-full p-3 hover:bg-warm-50 transition-colors text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-4 w-4 text-solution flex-shrink-0" />
                          <span className="text-sm font-medium text-warm-800 truncate">
                            {source.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-warm-500">
                          <span>{facetCount} insights</span>
                          {source.status === 'analyzed' && (
                            <Badge className="bg-green-100 text-green-700 text-[9px]">
                              <Sparkles className="h-2 w-2 mr-1" />
                              Analyzed
                            </Badge>
                          )}
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-warm-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-warm-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>

                  {/* Expanded facet list - fetches full source with facets */}
                  {isExpanded && (
                    <div className="border-t border-warm-200 p-3 bg-warm-50/50">
                      <SourceFacetsList
                        accountId={accountId}
                        projectId={projectId}
                        sourceId={source.id}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Sources panel (URLs and text) */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-warm-800">Sources</h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setShowAddSource(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>

        {sources.length === 0 ? (
          <div className="text-center py-6 text-warm-500">
            <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No sources added yet</p>
            <p className="text-xs mt-1">Add URLs or notes to reference</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sources.map((source) => (
              <div key={source.id} className="p-2 rounded border border-warm-200 hover:bg-warm-50">
                <div className="flex items-center gap-2">
                  {source.sourceType === 'document' ? (
                    <FileText className="h-4 w-4 text-warm-500" />
                  ) : source.sourceType === 'url' ? (
                    <Link2 className="h-4 w-4 text-warm-500" />
                  ) : (
                    <FileText className="h-4 w-4 text-warm-500" />
                  )}
                  <span className="text-sm text-warm-700 truncate flex-1">{source.title}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Document Dialog - Uses UnifiedDocumentUploader for facet-first analysis */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-solution" />
              Add Document
            </DialogTitle>
          </DialogHeader>

          <UnifiedDocumentUploader
            sourceContext="coach"
            onComplete={handleDocumentAnalyzed}
            onClose={() => setShowUploadDialog(false)}
            defaultContentType="custom"
            compact
          />
        </DialogContent>
      </Dialog>

      {/* Add Source Dialog */}
      <Dialog open={showAddSource} onOpenChange={setShowAddSource}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Source</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Source type tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setSourceType('url')}
                className={cn(
                  'flex-1 py-2 px-3 text-sm rounded-md border transition-colors',
                  sourceType === 'url'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-warm-200 text-warm-600 hover:bg-warm-50'
                )}
              >
                <Link2 className="h-4 w-4 inline mr-2" />
                URL
              </button>
              <button
                onClick={() => setSourceType('text')}
                className={cn(
                  'flex-1 py-2 px-3 text-sm rounded-md border transition-colors',
                  sourceType === 'text'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-warm-200 text-warm-600 hover:bg-warm-50'
                )}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                Text
              </button>
            </div>

            {/* Title input */}
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-1">Title</label>
              <input
                type="text"
                value={sourceTitle}
                onChange={(e) => setSourceTitle(e.target.value)}
                placeholder={
                  sourceType === 'url' ? 'e.g., Market Research Report' : 'e.g., Customer Interview Notes'
                }
                className="w-full px-3 py-2 border border-warm-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Content input */}
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-1">
                {sourceType === 'url' ? 'URL' : 'Content'}
              </label>
              {sourceType === 'url' ? (
                <input
                  type="url"
                  value={sourceContent}
                  onChange={(e) => setSourceContent(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-warm-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              ) : (
                <textarea
                  value={sourceContent}
                  onChange={(e) => setSourceContent(e.target.value)}
                  placeholder="Paste your notes, research, or any relevant text..."
                  rows={5}
                  className="w-full px-3 py-2 border border-warm-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSource(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSource}
              disabled={!sourceTitle.trim() || !sourceContent.trim() || isAddingSource}
            >
              {isAddingSource ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Source'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
